import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowRight, Lock, Info, Stethoscope, Clock } from "lucide-react";

const NOTES = [
  [Lock, "I tuoi dati restano tuoi.", "Le risposte vengono elaborate solo sul tuo dispositivo e non vengono salvate sui nostri server. Alla fine potrai scegliere tu se inviarmi i risultati per una consulenza. Puoi cancellarle quando vuoi."],
  [Clock, "Rispondi d'istinto.", "La prima risposta è di solito la più vera. Servono circa 5 minuti."],
  [Info, "Uno strumento educativo.", "Il check-up non è validato scientificamente e non è uno strumento diagnostico: offre una fotografia indicativa per capire da dove partire."],
  [Stethoscope, "Non sostituisce un professionista.", "Se attraversi un periodo di forte stress, ansia o difficoltà con il cibo, rivolgiti a un medico o a uno psicologo."],
];
import { Navbar } from "@/components/Navbar";
import { CartDrawer } from "@/components/CartDrawer";
import { Footer } from "@/components/Footer";
import { QuizQuestion } from "@/components/test/QuizQuestion";
import { TestResult } from "@/components/test/TestResult";
import { QUESTIONS, SKILLS, computeScores } from "@/lib/softskills";
import { Cite } from "@/components/Cite";

const KEY = "hhl_softskills_v1";
const load = () => {
  try {
    const v = JSON.parse(localStorage.getItem(KEY) || "null");
    return v && Array.isArray(v.answers) ? v : null;
  } catch {
    return null;
  }
};

const Intro = ({ onStart, resume }) => (
  <div data-testid="test-intro" className="mx-auto max-w-4xl">
    <p className="eyebrow mb-6">Check-up gratuito · 27 domande · 5 minuti</p>
    <h1 className="font-display text-6xl font-900 uppercase leading-[0.85] md:text-8xl">
      Quanto sono allenate le tue <span className="text-[#c4dcca]">soft skills?</span>
    </h1>
    <p className="mt-8 max-w-2xl text-base text-[#a3b1aa] md:text-lg">
      Il principale predittore dei risultati a lungo termine non è il programma, ma la capacità di aderirvi nel tempo.<Cite refs={["teixeira2012", "burke2011"]} /> Scopri il tuo profilo sulle 9 competenze del cambiamento: le soft skills e la capacità organizzativa.
    </p>
    <div className="mt-8 flex flex-wrap gap-2">
      {SKILLS.map((s) => <span key={s.key} className="border border-[rgba(152,191,162,0.25)] px-3 py-1.5 font-mono text-[11px] uppercase tracking-widest text-[#a3b1aa]">{s.short}</span>)}
    </div>
    <div data-testid="test-before-start" className="mt-12 grid gap-6 border border-[rgba(152,191,162,0.25)] bg-[#0b120e] p-6 md:grid-cols-2 md:p-8">
      <div>
        <p className="eyebrow mb-3">Prima di iniziare</p>
        <h2 className="font-display text-3xl font-900 uppercase leading-none">Sii il più sincero possibile.</h2>
        <p className="mt-3 text-sm leading-relaxed text-[#a3b1aa]">
          È un check-up di autovalutazione: non ci sono risposte giuste o sbagliate e nessuno ti giudica. Rispondi pensando a come ti comporti davvero nella maggior parte delle settimane, non a come vorresti essere. Solo così il risultato ti sarà utile.
        </p>
      </div>
      <ul className="space-y-3 text-sm text-[#a3b1aa]">
        {NOTES.map(([icon, title, text]) => {
          const Icon = icon;
          return (
            <li key={title} className="flex gap-3">
              <Icon size={16} className="mt-0.5 shrink-0 text-[#98bfa2]" />
              <span><span className="text-[#f4f7f5]">{title}</span> {text}</span>
            </li>
          );
        })}
      </ul>
    </div>
    <button data-testid="test-start-btn" onClick={onStart}
      className="group mt-12 flex items-center gap-3 bg-[#98bfa2] px-8 py-5 font-display text-xl font-800 uppercase text-[#050807] transition-colors hover:bg-[#c4dcca]">
      {resume ? "Riprendi il check-up" : "Inizia il check-up"} <ArrowRight className="transition-transform group-hover:translate-x-1" size={20} />
    </button>
  </div>
);

export default function SoftSkillsTest() {
  const saved = load();
  const [answers, setAnswers] = useState(saved?.answers ?? []);
  const [index, setIndex] = useState(saved?.index ?? 0);
  const [phase, setPhase] = useState(saved?.done ? "result" : "intro");

  useEffect(() => {
    if (!answers.length && phase === "intro") localStorage.removeItem(KEY);
    else localStorage.setItem(KEY, JSON.stringify({ answers, index, done: phase === "result" }));
  }, [answers, index, phase]);

  useEffect(() => {
    if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
    else window.scrollTo(0, 0);
  }, [index, phase]);

  const answer = (v) => {
    const next = [...answers];
    next[index] = v;
    setAnswers(next);
    if (index + 1 >= QUESTIONS.length) setPhase("result");
    else setTimeout(() => setIndex(index + 1), 180);
  };
  const restart = () => {
    setAnswers([]);
    setIndex(0);
    setPhase("quiz");
  };
  const clearAll = () => {
    setAnswers([]);
    setIndex(0);
    setPhase("intro");
    toast.success("Risposte cancellate da questo dispositivo");
  };
  const { results, overall } = computeScores(answers);

  return (
    <main data-testid="softskills-test-page">
      <Navbar />
      <CartDrawer />
      <section className="min-h-screen px-5 pb-24 pt-28 md:px-10 md:pt-36">
        <motion.div key={phase} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }}>
          {phase === "intro" && <Intro onStart={() => setPhase("quiz")} resume={answers.length > 0} />}
          {phase === "quiz" && <QuizQuestion index={index} value={answers[index]} onAnswer={answer} onBack={() => setIndex(Math.max(0, index - 1))} />}
          {phase === "result" && <TestResult results={results} overall={overall} onRestart={restart} onClear={clearAll} />}
        </motion.div>
      </section>
      <Footer />
    </main>
  );
}
